import { useState, useMemo } from 'react';
import {
  Plus,
  Loader2,
  Wrench,
  X,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import {
  useMaintenanceRequests,
  useMaintenanceRequest,
  useCreateMaintenance,
  useUpdateMaintenance,
} from '@/hooks/api/useMaintenance';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useUnits } from '@/hooks/api/useProperties';
import { useUsers } from '@/hooks/api/useUsers';
import type {
  MaintenanceRequestData,
  MaintenanceCategory,
  MaintenanceUrgency,
  MaintenanceRequestStatus,
  MaintenanceAuditLogEntry,
} from '@/services/api/maintenance.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<MaintenanceCategory, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  general: 'General',
  other: 'Other',
};

const URGENCY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  low: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Low' },
  medium: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Medium' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
  urgent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' },
};
const URGENCY_OPTIONS: MaintenanceUrgency[] = ['low', 'medium', 'high', 'urgent'];
const urgencyStyle = (u: unknown) => URGENCY_STYLES[String(u)] ?? URGENCY_STYLES.medium;

const STATUS_STYLES: Record<MaintenanceRequestStatus, { bg: string; text: string; label: string }> = {
  open: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Open' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'In Progress' },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancelled' },
};

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function fmtDateTime(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy h:mm a');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function MaintenancePage() {
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const propertyId = useSelectedPropertyId();

  const { data: listData, isLoading } = useMaintenanceRequests({
    category: filterCategory || undefined,
    status: filterStatus || undefined,
    propertyId: propertyId || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const allItems: MaintenanceRequestData[] = listData?.data ?? [];
  const totalPages = listData?.meta?.totalPages ?? 1;

  const items: MaintenanceRequestData[] = useMemo(() => {
    if (!searchTerm) return allItems;
    const q = searchTerm.toLowerCase();
    return allItems.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        (r.unitName ?? '').toLowerCase().includes(q),
    );
  }, [allItems, searchTerm]);

  return (
    <div>
      <PageHeader
        title="Maintenance"
        description="Track and manage maintenance requests"
        actions={
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus size={16} aria-hidden="true" />
            New Request
          </Button>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Filter by category"
        >
          <option value="">All Categories</option>
          {(Object.entries(CATEGORY_LABELS) as [MaintenanceCategory, string][]).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          {(Object.entries(STATUS_STYLES) as [MaintenanceRequestStatus, { label: string }][]).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search title or unit..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search maintenance requests"
        />
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Filter from date"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Filter to date"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title="No maintenance requests"
          description="Create a new request to get started."
          actionLabel="New Request"
          onAction={() => setShowCreateForm(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>ID</Th>
                  <Th>Unit</Th>
                  <Th>Category</Th>
                  <Th>Title</Th>
                  <Th>Urgency</Th>
                  <Th>Status</Th>
                  <Th>Assigned To</Th>
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((req) => {
                  const urg = urgencyStyle(req.urgency);
                  const stat = STATUS_STYLES[req.status];
                  return (
                    <tr
                      key={req.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedId(req.id)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{req.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{req.unitName ?? req.unitId.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{CATEGORY_LABELS[req.category]}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{req.title}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', urg.bg, urg.text)}>
                          {urg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', stat.bg, stat.text)}>
                          {stat.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{req.assigneeName ?? '--'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDate(req.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create form slide-over */}
      <SlideOver
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="New Maintenance Request"
      >
        <CreateMaintenanceForm onClose={() => setShowCreateForm(false)} />
      </SlideOver>

      {/* Detail slide-over */}
      <SlideOver
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title="Maintenance Request"
        width="max-w-xl"
      >
        {selectedId !== null && (
          <MaintenanceDetail id={selectedId} />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Create form ─────────────────────────────────────────────────────────────

function CreateMaintenanceForm({ onClose }: { onClose: () => void }) {
  const [unitId, setUnitId] = useState('');
  const [category, setCategory] = useState<MaintenanceCategory>('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState<MaintenanceUrgency>('medium');
  const [assignedTo, setAssignedTo] = useState('');

  const propertyId = useSelectedPropertyId();
  const { data: unitsData } = useUnits(propertyId || undefined);
  const units = Array.isArray(unitsData) ? unitsData : ((unitsData as any)?.data ?? []);

  const { data: usersData } = useUsers();
  const staffOptions = useMemo(() => {
    const list = Array.isArray(usersData) ? usersData : [];
    return list.filter((u: any) => u.isActive);
  }, [usersData]);

  const createMutation = useCreateMaintenance();

  function clearError() {
    if (createMutation.isError) createMutation.reset();
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(
      {
        unitId,
        category,
        title,
        description: description || undefined,
        urgency,
        assignedTo: assignedTo || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="mt-unit" className="block text-sm font-medium text-gray-700 mb-1">Unit <span className="text-red-500">*</span></label>
        <select id="mt-unit" required value={unitId} onChange={(e) => setUnitId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">Select a unit</option>
          {units.map((u: any) => (
            <option key={u.id} value={u.id}>{u.name} ({u.type})</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="mt-category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
        <select id="mt-category" value={category} onChange={(e) => setCategory(e.target.value as MaintenanceCategory)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
          {(Object.entries(CATEGORY_LABELS) as [MaintenanceCategory, string][]).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="mt-title" className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
        <input id="mt-title" type="text" required value={title} onChange={(e) => { setTitle(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="mt-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea id="mt-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="mt-urgency" className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
        <select id="mt-urgency" value={urgency} onChange={(e) => setUrgency(e.target.value as MaintenanceUrgency)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
          {URGENCY_OPTIONS.map((u) => (
            <option key={u} value={u}>{URGENCY_STYLES[u].label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="mt-assignee" className="block text-sm font-medium text-gray-700 mb-1">
          Assign To
        </label>
        <select
          id="mt-assignee"
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Unassigned</option>
          {staffOptions.map((u: any) => (
            <option key={u.id} value={u.id}>
              {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
              {u.role ? ` (${u.role})` : ''}
            </option>
          ))}
        </select>
      </div>
      {createMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending}>Create Request</Button>
      </div>
    </form>
  );
}

// ─── Detail view ─────────────────────────────────────────────────────────────

function MaintenanceDetail({ id }: { id: string }) {
  const { data: detail, isLoading } = useMaintenanceRequest(id);
  const updateMutation = useUpdateMaintenance();

  if (isLoading || !detail) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  const urg = urgencyStyle(detail.urgency);
  const stat = STATUS_STYLES[detail.status];

  const handleStatusChange = (newStatus: MaintenanceRequestStatus) => {
    updateMutation.mutate({ id: detail.id, payload: { status: newStatus } });
  };

  return (
    <div className="space-y-6">
      {/* Header info */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">{detail.title}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', stat.bg, stat.text)}>{stat.label}</span>
          <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', urg.bg, urg.text)}>{urg.label}</span>
          <span className="text-xs text-gray-400">|</span>
          <span className="text-xs text-gray-500">{CATEGORY_LABELS[detail.category]}</span>
        </div>
      </div>

      {/* Detail fields */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Unit</span>
          <p className="font-medium text-gray-900">{detail.unitName ?? detail.unitId.slice(0, 8)}</p>
        </div>
        <div>
          <span className="text-gray-500">Assigned To</span>
          <p className="font-medium text-gray-900">{detail.assigneeName ?? '--'}</p>
        </div>
        <div>
          <span className="text-gray-500">Created</span>
          <p className="font-medium text-gray-900">{fmtDateTime(detail.createdAt)}</p>
        </div>
        <div>
          <span className="text-gray-500">Updated</span>
          <p className="font-medium text-gray-900">{fmtDateTime(detail.updatedAt)}</p>
        </div>
      </div>

      {detail.description && (
        <div>
          <span className="text-sm text-gray-500">Description</span>
          <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{detail.description}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
        {detail.status === 'open' && (
          <Button
            size="sm"
            onClick={() => handleStatusChange('in_progress')}
            isLoading={updateMutation.isPending}
          >
            <Clock size={14} aria-hidden="true" />
            Mark In Progress
          </Button>
        )}
        {detail.status === 'in_progress' && (
          <Button
            size="sm"
            onClick={() => handleStatusChange('completed')}
            isLoading={updateMutation.isPending}
          >
            <CheckCircle2 size={14} aria-hidden="true" />
            Mark Completed
          </Button>
        )}
        {detail.status !== 'cancelled' && detail.status !== 'completed' && (
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleStatusChange('cancelled')}
            isLoading={updateMutation.isPending}
          >
            <X size={14} aria-hidden="true" />
            Cancel
          </Button>
        )}
      </div>

      {/* Audit log timeline */}
      {detail.auditLogs && detail.auditLogs.length > 0 && (
        <div className="pt-4 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Activity Log</h4>
          <div className="space-y-3">
            {detail.auditLogs.map((log: MaintenanceAuditLogEntry) => (
              <div key={log.id} className="flex items-start gap-3">
                <div className="mt-1 w-2 h-2 rounded-full bg-gray-300 shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{log.action}</span>
                  </p>
                  <p className="text-xs text-gray-400">{fmtDateTime(log.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Table helpers ───────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
